/**
 * Separator Component
 *
 * A simple horizontal or vertical divider line.
 *
 * @file Separator.tsx
 * @version 1.0.0 - November 2025
 */

import * as React from 'react';

import {cn} from '@/shared/lib/utils';

// =============================================================================
// TYPES
// =============================================================================

interface SeparatorProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The orientation of the separator.
   * @default "horizontal"
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Whether the separator is purely decorative.
   * @default true
   */
  decorative?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

const Separator = React.forwardRef<HTMLDivElement, SeparatorProps>(
  (
    { className, orientation = 'horizontal', decorative = true, ...props },
    ref
  ): React.ReactElement => {
    return (
      <div
        ref={ref}
        role={decorative ? 'none' : 'separator'}
        aria-orientation={decorative ? undefined : orientation}
        className={cn(
          'shrink-0 bg-border',
          orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px',
          className
        )}
        {...props}
      />
    );
  }
);

Separator.displayName = 'Separator';

export { Separator };
